/**
 * Dependent form fields. Optimizely decides whether a field's conditions are
 * met; this decides what that looks like.
 *
 * A field whose conditions are not met is rendered by the server with
 * Optimizely's `hide` class. The site's global `.hide` is
 * `display: none !important`, and the built-in action reveals a field with an
 * inline `display` — which that `!important` outranks, so the field never
 * appeared. Visibility moves onto the `hidden` attribute and the class comes
 * off with it.
 */

interface DependantInfo {
    fieldName: string;
    action?: { clientsideAction?: string };
}

interface WorkingFormInfo {
    DependencyInactiveElements: string[];
    DependantControllers?: DependantController[];
}

interface DependantController {
    isSatisfied: boolean;
    dependantInfo: DependantInfo;
    workingFormInfo: WorkingFormInfo;
    $domElement: ArrayLike<HTMLElement>;
}

const LIVE_REGION_ID = 'form-dependency-live-region';
const HIDE_CLASS = 'hide';

function formsApi(): any {
    const epi = (window as any).epi;
    return epi && epi.EPiServer && epi.EPiServer.Forms;
}

function fieldOf(controller: DependantController): HTMLElement | null {
    const dom = controller.$domElement;
    return dom && dom.length ? dom[0] : null;
}

function isVisible(controller: DependantController): boolean {
    const action = controller.dependantInfo && controller.dependantInfo.action;
    const name = (action && action.clientsideAction) || 'show';
    return name === 'hide' ? !controller.isSatisfied : !!controller.isSatisfied;
}

// Optimizely reads this list when it decides what to validate and what to post.
function markInactive(controller: DependantController, inactive: boolean): void {
    const info = controller.workingFormInfo;
    const name = controller.dependantInfo && controller.dependantInfo.fieldName;
    if (!info || !info.DependencyInactiveElements || !name) return;

    const list = info.DependencyInactiveElements;
    const at = list.indexOf(name);

    if (inactive) {
        if (at < 0) list.push(name);
        return;
    }
    for (let i = list.length - 1; i >= 0; i--) {
        if (list[i] === name) list.splice(i, 1);
    }
}

function announce(field: HTMLElement): void {
    const form = field.closest('.EPiServerForms') as HTMLElement;
    if (!form) return;

    let region = form.querySelector('#' + LIVE_REGION_ID) as HTMLElement;
    if (!region) {
        region = document.createElement('div');
        region.id = LIVE_REGION_ID;
        region.className = 'visually-hidden';
        region.setAttribute('aria-live', 'polite');
        form.appendChild(region);
    }

    const caption = field.querySelector('.Form__Element__Caption, .Form__Element__Legend');
    const label = caption ? caption.textContent.trim() : '';
    region.textContent = label ? label + ' is now available.' : 'An additional field is now available.';
}

function apply(controller: DependantController, visible: boolean): void {
    markInactive(controller, !visible);

    const field = fieldOf(controller);
    if (!field) return;

    const wasHidden = field.hidden || field.classList.contains(HIDE_CLASS);

    field.classList.remove(HIDE_CLASS);
    field.style.display = '';
    field.hidden = !visible;

    if (visible && wasHidden) announce(field);
}

function install(): boolean {
    const forms = formsApi();
    const dependency = forms && forms.Dependency;
    if (!dependency || !dependency.Actions) return false;

    dependency.Actions.show = (controller: DependantController) => apply(controller, !!controller.isSatisfied);
    dependency.Actions.hide = (controller: DependantController) => apply(controller, !controller.isSatisfied);
    return true;
}

function resync(): void {
    const forms = formsApi();
    if (!forms) return;

    const containers = document.querySelectorAll('.EPiServerForms, [data-f-type="form"]');
    Array.prototype.forEach.call(containers, (container: HTMLElement) => {
        const info: WorkingFormInfo = container.id ? forms[container.id] : null;
        const controllers = info && info.DependantControllers;
        if (!controllers) return;

        controllers.forEach(controller => apply(controller, isVisible(controller)));
    });
}

// The bundle runs ahead of Optimizely's scripts, whose setup waits on jQuery's
// ready, so the actions are in place before the first field is decided.
document.addEventListener('DOMContentLoaded', install);

window.addEventListener('load', () => {
    install();
    resync();
});
